import React, { useState, useEffect, useRef } from 'react'
import Lottie, { type LottieRefCurrentProps } from 'lottie-react'
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts'
import sparkleAnimation from '@/assets/sparkle.json'
import asfaltLight from '@/assets/asfalt-light.png'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Users,
  AlertTriangle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Sparkles,
  ChevronRight
} from 'lucide-react'
import { getDaysRemaining } from '@/lib/utils'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import NotificationCenter from '@/components/common/NotificationCenter'
import type { DashboardMetrics } from '@/types'

export default function DashboardScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSparkle, setShowSparkle] = useState(false)
  const [aiInsight, setAiInsight] = useState<{ hasData: boolean; title: string; body: string; isToday: boolean; label: string; updatedAt?: string } | null>(null)
  const [isAiInsightLoading, setIsAiInsightLoading] = useState(true)
  const lottieRef = useRef<LottieRefCurrentProps>(null)

  useEffect(() => {
    if (currentRestaurant?._id) {
      loadDashboardMetrics()
    }
  }, [currentRestaurant?._id])

  // Gemini-generated snapshot loads separately from the raw metrics — it's
  // slower (an LLM call) and shouldn't hold up the rest of the dashboard.
  useEffect(() => {
    if (!currentRestaurant?._id) return
    let cancelled = false
    const load = (showLoading: boolean) => {
      if (showLoading) setIsAiInsightLoading(true)
      ApiService.getDashboardAiInsight(currentRestaurant._id)
        .then(response => {
          if (!cancelled && response.success) setAiInsight(response.data)
        })
        .catch(() => { /* falls back to the plain snapshot below */ })
        .finally(() => { if (!cancelled) setIsAiInsightLoading(false) })
    }
    load(true)
    // Re-fetch hourly while the dashboard stays open (server regenerates at most once/hour)
    const timer = setInterval(() => load(false), 60 * 60 * 1000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [currentRestaurant?._id])

  // Fire sparkle animation once per calendar day (persists across PWA suspend/resume)
  useEffect(() => {
    if (!isLoading && metrics) {
      const key = 'dashboard_sparkle_date'
      const today = new Date().toDateString()
      if (sessionStorage.getItem(key) === today) return
      sessionStorage.setItem(key, today)

      const showTimer = setTimeout(() => setShowSparkle(true), 300)
      const dismissTimer = setTimeout(() => setShowSparkle(false), 6000)
      return () => {
        clearTimeout(showTimer)
        clearTimeout(dismissTimer)
      }
    }
  }, [isLoading, metrics])

  const loadDashboardMetrics = async () => {
    if (!currentRestaurant?._id) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ApiService.getDashboardMetrics(currentRestaurant._id)
      if (response.success) {
        setMetrics(response.data)
      } else {
        setError(response.message || 'Failed to load dashboard metrics')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dashboard metrics')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading dashboard...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2"
              onClick={loadDashboardMetrics}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No dashboard data available. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Get subscription info from user context
  const daysRemaining = user?.subscriptionEndDate ? getDaysRemaining(new Date(user.subscriptionEndDate)) : 0
  const isExpiringSoon = daysRemaining <= 30 && daysRemaining > 0
  const subscriptionStatus = user?.subscriptionStatus || 'active'

  // AI banner — build today's snapshot from real data
  const aiSnapshot = (() => {
    const revenue = metrics?.dailyRevenue || 0
    const orders = metrics?.dailyBillsCount || 0
    if (revenue === 0 || orders === 0) return null

    const peakHourNum = metrics?.peakHour
    let peakStr = ''
    if (peakHourNum !== undefined && peakHourNum !== null) {
      const period = peakHourNum >= 12 ? 'PM' : 'AM'
      const h = peakHourNum % 12 || 12
      const nh = (peakHourNum + 1) % 12 || 12
      const np = (peakHourNum + 1) >= 12 ? 'PM' : 'AM'
      peakStr = period === np ? `Peak was ${h}–${nh} ${period}.` : `Peak was ${h} ${period}–${nh} ${np}.`
    }

    return {
      revenueStr: formatCurrency(revenue),
      orders,
      peakStr,
      topItem: metrics?.topItem || null
    }
  })()

  const hasHourlyTrend = (metrics.hourlyRevenue || []).some(h => h.revenue > 0)
  const peakRevenueHour = hasHourlyTrend
    ? metrics.hourlyRevenue!.reduce((max, h) => (h.revenue > max.revenue ? h : max))
    : null
  const formatHour12 = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM'
    const h = hour % 12 || 12
    return `${h} ${period}`
  }

  const metricCards = [
    {
      title: 'Daily Revenue',
      value: formatCurrency(metrics.dailyRevenue || 0),
      description: 'Today\'s total revenue',
      icon: DollarSign,
      trend: metrics.dailyRevenueGrowth ? `${metrics.dailyRevenueGrowth > 0 ? '+' : ''}${metrics.dailyRevenueGrowth.toFixed(1)}%` : '',
      trendColor: (metrics.dailyRevenueGrowth || 0) >= 0 ? 'text-green-600' : 'text-red-600',
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(metrics.monthlyRevenue || 0),
      description: 'This month\'s revenue',
      icon: TrendingUp,
      trend: metrics.monthlyRevenueGrowth ? `${metrics.monthlyRevenueGrowth > 0 ? '+' : ''}${metrics.monthlyRevenueGrowth.toFixed(1)}%` : '',
      trendColor: (metrics.monthlyRevenueGrowth || 0) >= 0 ? 'text-green-600' : 'text-red-600',
    },
    {
      title: 'Daily Bills',
      value: (metrics.dailyBillsCount || 0).toString(),
      description: 'Orders completed today',
      icon: ShoppingCart,
      trend: metrics.totalBillsGrowth ? `${metrics.totalBillsGrowth > 0 ? '+' : ''}${metrics.totalBillsGrowth.toFixed(1)}%` : '',
      trendColor: (metrics.totalBillsGrowth || 0) >= 0 ? 'text-green-600' : 'text-red-600',
    },
    {
      title: 'Average Order Value',
      value: formatCurrency(metrics.averageBillValue || 0),
      description: 'Average per order',
      icon: Users,
      trend: metrics.averageOrderGrowth ? `${metrics.averageOrderGrowth > 0 ? '+' : ''}${metrics.averageOrderGrowth.toFixed(1)}%` : '',
      trendColor: (metrics.averageOrderGrowth || 0) >= 0 ? 'text-green-600' : 'text-red-600',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Sparkle Lottie — mobile only, plays once on dashboard load */}
      {showSparkle && (
        <div
          className="pointer-events-none fixed z-50"
          style={{ top: 0, left: 0, width: '100vw', height: '100vh' }}
        >
          <div className="w-full h-full scale-[1.6] sm:scale-100 origin-center overflow-visible">
            <Lottie
              lottieRef={lottieRef}
              animationData={sparkleAnimation}
              loop={false}
              autoplay={true}
              renderer="svg"
              onComplete={() => setShowSparkle(false)}
              style={{ width: '100vw', height: '100vh' }}
            />
          </div>
        </div>
      )}

      {/* Subscription Status Banner */}
      {isExpiringSoon && (
        <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="text-sm sm:text-base font-medium text-orange-800 dark:text-orange-200">
                    Subscription Expiring Soon
                  </p>
                  <p className="text-xs sm:text-sm text-orange-600 dark:text-orange-300">
                    Your subscription expires in {daysRemaining} days. Contact admin to renew.
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full sm:w-auto">
                Contact Admin
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Welcome back, {user?.name}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Here's your restaurant's performance overview
          </p>
        </div>
      </div>

      {/* AI-Powered Notifications Banner */}
      <div className="relative rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-neutral-900 p-4 sm:p-5 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-red-500" />
          <span className="text-sm sm:text-base font-semibold text-[#333] dark:text-gray-100">
            AI-Powered Daily Insights
          </span>
          <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-500/20 border border-red-300 dark:border-red-400/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
            Powered by ordore AI
          </span>
        </div>

        {/* Today's snapshot + revenue trend */}
        <div className="grid grid-cols-1 sm:grid-cols-[3fr_2fr] gap-3">
          <div
            className="flex items-center gap-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 px-4 py-3.5"
            style={{ backgroundImage: `url(${asfaltLight})` }}
          >
            <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-full bg-red-100 dark:bg-red-500/20">
              <TrendingUp className="h-4 w-4 text-red-600 dark:text-red-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                {aiInsight?.hasData && !aiInsight.isToday ? `${aiInsight.label}'s update` : "Today's snapshot"}
                {aiInsight?.hasData && aiInsight.updatedAt && (
                  <span className="text-[10px] opacity-75">
                    · Updated {new Date(aiInsight.updatedAt).toLocaleString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, ...(aiInsight.isToday ? {} : { day: 'numeric', month: 'short' }) })}
                  </span>
                )}
              </p>
              {aiInsight?.hasData && aiInsight.body ? (
                <div className="space-y-0.5">
                  {aiInsight.body.split(/(?<=[.!?])\s+/).filter(Boolean).map((sentence, i) => (
                    <p key={i} className="text-sm sm:text-base font-medium text-[#333] dark:text-gray-100 leading-snug">
                      {sentence}
                    </p>
                  ))}
                </div>
              ) : aiSnapshot ? (
                <>
                  <p className="text-base sm:text-lg font-bold text-[#333] dark:text-gray-100 leading-snug">
                    {aiSnapshot.revenueStr} from <span className="text-red-600 dark:text-red-400">{aiSnapshot.orders}</span> order{aiSnapshot.orders !== 1 ? 's' : ''} so far today.
                  </p>
                  {(aiSnapshot.peakStr || aiSnapshot.topItem) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {aiSnapshot.peakStr}
                      {aiSnapshot.peakStr && aiSnapshot.topItem ? ' ' : ''}
                      {aiSnapshot.topItem && (
                        <>Best seller: <span className="font-medium text-[#333] dark:text-gray-200">{aiSnapshot.topItem.name}</span> ({aiSnapshot.topItem.quantity} sold).</>
                      )}
                    </p>
                  )}
                  {isAiInsightLoading && (
                    <p className="text-[10px] text-muted-foreground/70 mt-1 italic">Getting the AI take on today...</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-[#333] dark:text-gray-300">
                  Once your orders come in, today's snapshot will show up here.
                </p>
              )}
            </div>
          </div>

          {/* Minimal, background-less hourly revenue sparkline — no axes, no
              gridlines, no plot-area fill. Just the trend line + a hover tooltip. */}
          <div className="rounded-lg border border-gray-100 dark:border-white/10 px-4 py-3.5 flex flex-col">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-xs text-muted-foreground">Revenue trend today</p>
              {peakRevenueHour && (
                <p className="text-[10px] font-medium text-red-500 whitespace-nowrap">
                  Peak {formatHour12(peakRevenueHour.hour)}
                </p>
              )}
            </div>
            {hasHourlyTrend ? (
              <div className="flex-1 min-h-[56px]">
                <ResponsiveContainer width="100%" height={56}>
                  <LineChart data={metrics.hourlyRevenue} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                    <Tooltip
                      cursor={false}
                      contentStyle={{
                        background: 'var(--popover, #fff)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 6,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                        padding: '4px 8px',
                        fontSize: 11
                      }}
                      labelFormatter={(hour) => `${hour}:00`}
                      formatter={(value) => [formatCurrency(Number(value) || 0), 'Revenue']}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3, fill: '#ef4444', stroke: 'none' }}
                      isAnimationActive={true}
                      animationDuration={1200}
                      animationEasing="ease-out"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-[#333] dark:text-gray-300 flex-1 flex items-center">
                Trend will appear once today's orders come in.
              </p>
            )}
          </div>
        </div>

        <p className="flex items-start gap-1.5 text-xs text-muted-foreground mt-3">
          Every hour, ordore AI analyses your day and sends a personalised summary — peak hours, top dishes &amp; what to do tomorrow.
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        {metricCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {card.title}
              </CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold">{card.value}</div>
              <p className="text-xs text-muted-foreground">
                {card.description}
              </p>
              <div className="flex items-center space-x-1 mt-2">
                <span className={`text-xs ${card.trendColor}`}>
                  {card.trend}
                </span>
                <span className="text-xs text-muted-foreground">
                  from last period
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick Analytics</CardTitle>
            <CardDescription>
              View detailed analytics and trends
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/analytics')}>
              View Analytics
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sales Reports</CardTitle>
            <CardDescription>
              Generate and download sales reports
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => navigate('/sales-reports')}>
              View Reports
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profit Analysis</CardTitle>
            <CardDescription>
              Analyze profit margins and costs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => navigate('/profit-analysis')}>
              View Analysis
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Today's Summary</CardTitle>
          <CardDescription>
            Key highlights from today's performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
            <div className="text-center p-3 sm:p-4 rounded-lg bg-muted">
              <div className="text-lg sm:text-2xl font-bold text-green-600">
                {currencySymbol}{(metrics.dailyRevenue / 1000).toFixed(0)}K
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">Revenue</div>
            </div>
            <div className="text-center p-3 sm:p-4 rounded-lg bg-muted">
              <div className="text-lg sm:text-2xl font-bold text-blue-600">
                {metrics.dailyBillsCount}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">Orders</div>
            </div>
            <div className="text-center p-3 sm:p-4 rounded-lg bg-muted">
              <div className="text-lg sm:text-2xl font-bold text-purple-600">
                {formatCurrency(metrics.averageBillValue)}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">Avg Order</div>
            </div>
            <div className="text-center p-3 sm:p-4 rounded-lg bg-muted">
              <div className="text-lg sm:text-2xl font-bold text-orange-600">
                {Math.round(metrics.dailyRevenue / metrics.averageBillValue)}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">Customers</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Center */}
      <NotificationCenter />
    </div>
  )
}